import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Check, Copy } from '@saleshandy/icons';
import { OverlayTooltip, Placement } from '../design-system/components/overlay';

const COPY_ICON_SIZE = 14;

interface CopyButtonProps {
  text: string;
  copyTooltipText?: string;
  resetDelay?: number;
  placement?: Placement;
  className?: string;
}

const CopyButton: React.FC<CopyButtonProps> = ({
  text,
  copyTooltipText = 'Click to copy',
  resetDelay = 500,
  placement = Placement.Top,
  className = '',
}) => {
  const [isCopied, setIsCopied] = useState(false);
  const resetCopiedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  const handleCopy = useCallback(
    (e: React.MouseEvent | React.KeyboardEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (isCopied) return;
      if (typeof navigator === 'undefined' || !navigator.clipboard?.writeText) {
        return;
      }
      navigator.clipboard.writeText(text).then(
        () => {
          setIsCopied(true);
          if (resetCopiedTimeoutRef.current) {
            clearTimeout(resetCopiedTimeoutRef.current);
          }
          resetCopiedTimeoutRef.current = setTimeout(() => {
            setIsCopied(false);
            resetCopiedTimeoutRef.current = null;
          }, resetDelay);
        },
        () => {
          // Clipboard may be denied; keep icon unchanged
        },
      );
    },
    [isCopied, text, resetDelay],
  );

  useEffect(
    () => () => {
      if (resetCopiedTimeoutRef.current) {
        clearTimeout(resetCopiedTimeoutRef.current);
      }
    },
    [],
  );

  return (
    <OverlayTooltip
      placement={placement}
      text={isCopied ? 'Copied' : `${copyTooltipText}`}
      className={`click-to-copy-tooltip ${className}`}
      rePositionOnChange
      rootClose
    >
      <span
        className="copy-button-icon cursor-pointer"
        role="button"
        tabIndex={0}
        aria-pressed={isCopied}
        aria-label={isCopied ? 'Copied' : copyTooltipText}
        onClick={handleCopy}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') handleCopy(e);
        }}
      >
        {isCopied ? (
          <Check height={COPY_ICON_SIZE} width={COPY_ICON_SIZE} />
        ) : (
          <Copy height={COPY_ICON_SIZE} width={COPY_ICON_SIZE} />
        )}
      </span>
    </OverlayTooltip>
  );
};

export default CopyButton;
